import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { ApiError } from "../api/client";
import { useLogin } from "../api/auth";
import { AuthShell } from "../components/AuthShell";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useLogin();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await login.mutateAsync({ email, password });
      navigate(searchParams.get("next") ?? "/");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 423) {
          setError("Compte temporairement bloqué. Réessayez dans quelques minutes.");
        } else if (err.status === 401) {
          setError("Email ou mot de passe incorrect.");
        } else {
          setError("Erreur de connexion. Réessayez.");
        }
      }
    }
  };

  return (
    <AuthShell
      title="Connexion"
      subtitle="Entrez vos identifiants pour accéder à votre organisation."
      footer={
        <Link
          to="/forgot-password"
          className="text-ink dark:text-ink-dark underline underline-offset-2 hover:text-ink/70 dark:hover:text-ink-dark/70"
        >
          Mot de passe oublié ?
        </Link>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs text-muted dark:text-muted-dark block mb-1" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bun-textbox w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-ink/30 dark:focus:ring-white/30"
          />
        </div>
        <div>
          <label className="text-xs text-muted dark:text-muted-dark block mb-1" htmlFor="password">
            Mot de passe
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bun-textbox w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-ink/30 dark:focus:ring-white/30"
          />
        </div>
        {error && (
          <div className="text-sm text-chip-red" role="alert">{error}</div>
        )}
        <button
          type="submit"
          disabled={login.isPending}
          className="w-full px-4 py-2 rounded-full bg-ink text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-ink/90 disabled:opacity-50"
        >
          {login.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Se connecter
        </button>
      </form>
    </AuthShell>
  );
}
